import _ from 'lodash';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import Logger from './Logger';
import { isElectron } from './desktopClient';

const ORGANIZATION_FRAGMENT = gql(`
  fragment initAnalytics_organization on Organization {
    id
    name
    host
    planTier
    billingStatus
    insertedAt
    trialEndAt
  }
`);

const USER_FRAGMENT = gql(`
  fragment initAnalytics_user on User {
    id
    name
    email
    type
    insertedAt
    settings {
      allowAccountAccess
    }
  }
`);

export function initAnalytics(
  organization: DocumentType<typeof ORGANIZATION_FRAGMENT>,
  user: DocumentType<typeof USER_FRAGMENT>,
  intercomHash: string,
) {
  const { LogRocket, analytics, __slab } = window;
  try {
    if (LogRocket && process.env.NODE_ENV === 'production') {
      const config: { release: string; rootHostname?: string } = {
        release: __slab.VERSION,
      };
      if (document.location.host.endsWith(__slab.HOST)) {
        config.rootHostname = __slab.HOST;
      }
      LogRocket.init('mh8kbn/slab', config);
      LogRocket.identify(user.id, {
        name: user.name,
        email: user.email,
        company: organization.name,
        companyId: organization.id,
        host: organization.host,
      });
    }
  } catch (e) {
    console.error(e); // eslint-disable-line no-console
  }

  try {
    const companyInfo = {
      name: organization.name,
      host: organization.host,
      plan: _.toLower(organization.planTier ?? ''), // plan has special meaning in Intercom
      billingStatus: _.toLower(organization.billingStatus ?? ''),
      createdAt: organization.insertedAt,
      trialEndAt: organization.trialEndAt,
    };
    const userInfo = {
      name: user.name,
      email: user.email,
      type: _.toLower(user.type ?? ''),
      allowAccountAccess: user.settings?.allowAccountAccess,
      createdAt: user.insertedAt,
      company: {
        id: organization.id,
        ...companyInfo,
      },
    };

    analytics.identify(user.id, userInfo, {
      Intercom: { hideDefaultLauncher: true, user_hash: intercomHash },
      ...(isElectron
        ? {
            context: {
              app: {
                name: 'Slab',
                version: window.desktop?.version || '',
              },
            },
          }
        : undefined),
    });

    analytics.group(organization.id, companyInfo);
  } catch (e) {
    Logger.error(e, { source: 'segment analytics.js context' });
  }
}

export function track(...args: unknown[]) {
  const { LogRocket, analytics } = window;
  analytics.track(...args);
  if (LogRocket && process.env.NODE_ENV === 'production') {
    LogRocket.track(...args);
  }
}
